export const BAR_COUNT = 180
export const BAR_FLOOR = 0.08
export const BAR_AMPLITUDE_FALLBACK_SEED = 9
/** Must match `.bar` min-width and `.barsRow` gap in styles.module.css. */
export const BAR_MIN_PX = 1
export const BAR_GAP_PX = 1

/**
 * How many bars of `minBarPx` with `gapPx` between them fit in `widthPx`.
 * Capped at `max` (the decoded bin count). A non-positive width yields 1 so
 * the row never renders empty before it has been measured.
 */
export function barCountForWidth(
  widthPx: number,
  gapPx: number = BAR_GAP_PX,
  minBarPx: number = BAR_MIN_PX,
  max: number = BAR_COUNT,
): number {
  if (!Number.isFinite(widthPx) || widthPx <= 0) return 1
  const slot = minBarPx + gapPx
  if (!(slot > 0)) return 1
  // Leave 1px of slack so subpixel rounding cannot push the last bar outside.
  const available = Math.max(0, widthPx - 1)
  const count = Math.floor((available + gapPx) / slot)
  const capped = Math.min(max, count)
  return capped > 0 ? capped : 1
}

/**
 * Downsample `peaks` to `count` bars. Each bar is the mean of the source bins
 * it covers. The bins partition the whole array, so the last bar always
 * includes the end of the recording.
 */
export function resamplePeaks(peaks: readonly number[], count: number): number[] {
  if (count <= 0) return []
  if (peaks.length === 0) return new Array<number>(count).fill(0)
  if (count === peaks.length) return peaks.slice()
  const out = new Array<number>(count)
  for (let i = 0; i < count; i++) {
    const start = Math.floor((i * peaks.length) / count)
    const end = Math.min(peaks.length, Math.max(start + 1, Math.floor(((i + 1) * peaks.length) / count)))
    let sum = 0
    for (let j = start; j < end; j++) sum += peaks[j] ?? 0
    out[i] = sum / (end - start)
  }
  return out
}

/** Click x → 0..1 across the full waveform width. Either edge clamps to the
 * start or the end of the recording. */
export function seekFraction(clientX: number, left: number, width: number): number {
  if (!Number.isFinite(clientX) || !Number.isFinite(left) || !Number.isFinite(width) || width <= 0) return 0
  const raw = (clientX - left) / width
  if (raw <= 0) return 0
  if (raw >= 1) return 1
  return raw
}

// Stable keys for the fixed-length waveform bar array — position IS the
// identity for a bar, so we precompute keys instead of using the loop index.
export const BAR_KEYS: readonly string[] = Array.from({ length: BAR_COUNT }, (_, n) => `bar-${n}`)

/** Deterministic pseudo-random heights matching the design's fallback look —
 * used while audio decoding is in flight (or if it fails). */
export function fallbackBarHeights(): number[] {
  const out = new Array<number>(BAR_COUNT)
  let s = BAR_AMPLITUDE_FALLBACK_SEED
  for (let i = 0; i < BAR_COUNT; i++) {
    s = (s * 9301 + 49297) % 233280
    const r = s / 233280
    const env = 0.4 + 0.5 * Math.abs(Math.sin(i / 8)) + 0.2 * Math.sin(i / 23)
    out[i] = Math.max(BAR_FLOOR, Math.min(1, env * (0.5 + r * 0.6)))
  }
  return out
}

export interface IDecodeResult {
  bins: number[]
  /** Duration in seconds reported by the decoded buffer. Authoritative for
   * WebM/Opus blobs from MediaRecorder, where the container header has no
   * duration and the `<audio>` element returns Infinity until buffered. */
  durationSec: number | null
}

/** Fetch `res`'s body to an ArrayBuffer, reporting 0..1 download progress from
 * Content-Length as chunks arrive. Falls back to a single buffered read when
 * the body isn't streamable or the length is unknown. */
export async function readBodyWithProgress(res: Response, onProgress?: (p: number) => void): Promise<ArrayBuffer> {
  const total = Number(res.headers.get("Content-Length") ?? "0")
  if (!res.body || !(total > 0)) {
    const buf = await res.arrayBuffer()
    onProgress?.(1)
    return buf
  }
  const reader = res.body.getReader()
  const chunks: Uint8Array[] = []
  let received = 0
  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    if (!value) continue
    chunks.push(value)
    received += value.length
    onProgress?.(Math.min(1, received / total))
  }
  const out = new Uint8Array(received)
  let offset = 0
  for (const chunk of chunks) {
    out.set(chunk, offset)
    offset += chunk.length
  }
  return out.buffer
}

/** Decode an audio file and downsample to BAR_COUNT amplitude bins (RMS per
 * bin, normalised to [0,1]). Returns the decoded buffer's duration too, and
 * reports 0..1 download progress as the file streams in. */
export async function decodeAudio(src: string, onProgress?: (p: number) => void): Promise<IDecodeResult> {
  try {
    const res = await fetch(src)
    if (!res.ok) throw new Error(`fetch ${res.status}`)
    const buf = await readBodyWithProgress(res, onProgress)
    const Ctor =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
    const ctx = new Ctor()
    const decoded = await ctx.decodeAudioData(buf)
    const channel = decoded.getChannelData(0)
    const binSize = Math.floor(channel.length / BAR_COUNT) || 1
    const bins = new Array<number>(BAR_COUNT)
    let max = 0
    for (let i = 0; i < BAR_COUNT; i++) {
      let sum = 0
      const start = i * binSize
      const end = Math.min(start + binSize, channel.length)
      for (let j = start; j < end; j++) {
        const v = channel[j]
        sum += v * v
      }
      const rms = Math.sqrt(sum / Math.max(1, end - start))
      bins[i] = rms
      if (rms > max) max = rms
    }
    if (max > 0) {
      for (let i = 0; i < BAR_COUNT; i++) {
        bins[i] = Math.max(BAR_FLOOR, Math.min(1, bins[i] / max))
      }
    }
    const durationSec = Number.isFinite(decoded.duration) && decoded.duration > 0 ? decoded.duration : null
    await ctx.close().catch(() => {})
    return { bins: max > 0 ? bins : fallbackBarHeights(), durationSec }
  } catch {
    return { bins: fallbackBarHeights(), durationSec: null }
  }
}
